import { useState, useEffect } from 'react';
import {
  Briefcase,
  GraduationCap,
  BookOpen,
  MapPin,
  Building2,
  X,
  Plane,
  Heart,
  Award,
  CheckCircle2,
  Target,
  Loader2,
  FileText,
} from 'lucide-react';
import { usePortfolio } from '@contexts/PortfolioContext';
import { useNavigate } from 'react-router-dom';
import portfolioService from '@services/portfolio.service';
import FresherPortfolio from './FresherPortfolio';
import ProfessionalPortfolio from './ProfessionalPortfolio';
import { usePlanRestrictions } from '@hooks/usePlanRestrictions';
import { filterExamCategoriesByPlan } from '@utils/planRestrictions';

/* ─────────────────────────────────────────────
   Shared small components
───────────────────────────────────────────── */

const SectionCard = ({ icon: Icon, iconBg, iconColor, title, subtitle, children }) => (
  <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
    <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
      <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: iconBg }}>
        <Icon className="w-4 h-4" style={{ color: iconColor }} />
      </div>
      <div className="min-w-0">
        <p className="text-sm font-semibold text-gray-900 leading-tight">{title}</p>
        {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
      </div>
    </div>
    <div className="p-4 space-y-3">{children}</div>
  </div>
);

const FieldLabel = ({ children }) => (
  <label className="block text-xs font-medium text-gray-700 mb-1">
    {children}
  </label>
);

const StyledSelect = ({ value, onChange, children, className = '' }) => (
  <select
    value={value}
    onChange={onChange}
    className={`w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 text-sm focus:outline-none focus:ring-1 focus:ring-primary-400 focus:border-primary-400 transition-colors ${className}`}
  >
    {children}
  </select>
);

const Chip = ({ active, onClick, children, colorClass = 'red' }) => {
  const activeStyles =
    colorClass === 'navbar'
      ? 'bg-navbar border-navbar text-white'
      : colorClass === 'blue'
      ? 'bg-blue-600 border-blue-600 text-white'
      : 'bg-primary-600 border-primary-600 text-white';

  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-2.5 py-1 rounded-md text-xs font-medium border transition-all ${
        active
          ? activeStyles + ' shadow-sm'
          : 'bg-white border-gray-300 text-gray-700 hover:border-gray-400 hover:bg-gray-50'
      }`}
    >
      {children}
    </button>
  );
};

const RemoveTag = ({ label, onRemove, colorClass = 'red' }) => {
  const styles =
    colorClass === 'navbar'
      ? 'bg-navbar border-navbar text-white'
      : colorClass === 'blue'
      ? 'bg-blue-50 border-blue-200 text-blue-700'
      : 'bg-[#c33c3d] border-[#c33c3d] text-white';

  return (
    <span
      className={`inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1 rounded-full text-xs font-medium border ${styles}`}
    >
      {label}
      <button
        type="button"
        onClick={onRemove}
        className="w-4 h-4 rounded-full flex items-center justify-center hover:bg-black/10"
        aria-label={`Remove ${label}`}
      >
        <X className="w-2.5 h-2.5" />
      </button>
    </span>
  );
};

const LoadingRow = ({ text = 'Loading…' }) => (
  <div className="flex items-center justify-center py-8 gap-2">
    <Loader2 className="w-4 h-4 text-[#c33c3d] animate-spin" />
    <span className="text-xs text-gray-500">{text}</span>
  </div>
);

const ErrorMsg = ({ text }) => (
  <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{text}</p>
);

const EmptyMsg = ({ text }) => (
  <p className="text-xs text-gray-400 text-center border border-dashed border-gray-200 rounded-xl py-6">
    {text}
  </p>
);

/* ─────────────────────────────────────────────
   Exam card
───────────────────────────────────────────── */
const ExamCard = ({ exam, isSelected, onToggle, accentColor = '#c33c3d', accentBg = '#fee2e2', tagBg = '#fee2e2', tagText = '#c33c3d' }) => (
  <button
    type="button"
    onClick={() => onToggle(exam)}
    className={`relative text-left rounded-lg border p-3 transition-all duration-200 ${
      isSelected
        ? 'shadow-sm border-2'
        : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
    }`}
    style={
      isSelected
        ? { borderColor: accentColor, background: accentBg + '40' }
        : {}
    }
  >
    {/* Checkbox indicator */}
    <div
      className="absolute top-2.5 right-2.5 w-5 h-5 rounded-md flex items-center justify-center transition-all"
      style={
        isSelected
          ? { background: accentColor, borderColor: accentColor, border: `2px solid ${accentColor}` }
          : { background: 'white', border: '2px solid #d1d5db' }
      }
    >
      {isSelected && (
        <svg className="w-3 h-3 text-white" viewBox="0 0 20 20" fill="currentColor">
          <path
            fillRule="evenodd"
            d="M16.704 5.296a1 1 0 010 1.414l-7.5 7.5a1 1 0 01-1.414 0l-3.5-3.5a1 1 0 011.414-1.414L8.5 12.086l6.79-6.79a1 1 0 011.414 0z"
            clipRule="evenodd"
          />
        </svg>
      )}
    </div>

    {isSelected && (
      <span
        className="inline-flex items-center gap-1 mb-1.5 px-2 py-0.5 rounded text-[10px] font-semibold text-white"
        style={{ background: accentColor }}
      >
        <CheckCircle2 className="w-2.5 h-2.5" />
        Selected
      </span>
    )}

    <h4 className="text-sm font-semibold text-gray-900 pr-6 leading-snug">
      {exam.examName}
      {exam.fullForm && <span className="font-normal text-gray-600 text-xs"> ({exam.fullForm})</span>}
    </h4>

    <p className="text-xs font-medium mt-1" style={{ color: accentColor }}>
      {exam.category}
    </p>

    {exam.purpose && (
      <p className="text-[11px] text-gray-500 mt-2 leading-relaxed line-clamp-2">{exam.purpose}</p>
    )}

    {exam.eligibility && (
      <div className="flex flex-wrap gap-1.5 mt-2.5">
        {exam.eligibility
          .split(/[+,]/)
          .map((s) => s.trim())
          .filter(Boolean)
          .map((tag, idx) => (
            <span
              key={idx}
              className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-medium"
              style={{ background: tagBg, color: tagText }}
            >
              {tag}
            </span>
          ))}
      </div>
    )}
  </button>
);

/* ─────────────────────────────────────────────
   Progress bar
───────────────────────────────────────────── */
const ProgressBar = ({ value }) => (
  <div className="h-1.5 w-full bg-white/20 rounded-full overflow-hidden">
    <div
      className="h-full bg-white rounded-full transition-all duration-500 shadow-sm"
      style={{ width: `${Math.min(100, value)}%` }}
    />
  </div>
);

/* ─────────────────────────────────────────────
   Main StudentPortfolio
───────────────────────────────────────────── */
const StudentPortfolio = () => {
  const navigate = useNavigate();
  const {
    careerStage,
    selectedCourse,
    selectedSpecialisation,
    selectedSubjects,
    coursePreference,
    abroadEducation,
    medicalIssue,
    selectedState,
    selectedUniversities,
    examType,
    customExam,
    selectedExams,
    selectedAptitudeTopics,
    opportunityType,
    courses,
    specialisations,
    subjects,
    states,
    universities,
    updateCourse,
    updateSpecialisation,
    updateSubjects,
    updateCoursePreference,
    updateAbroadEducation,
    updateMedicalIssue,
    updateState,
    updateUniversities,
    toggleUniversity,
    updateExamType,
    updateCustomExam,
    toggleExam,
    updateSelectedExams,
    toggleAptitudeTopic,
    updateSelectedAptitudeTopics,
    updateOpportunityType,
  } = usePortfolio();

  const { currentPlan } = usePlanRestrictions();

  const [examCategories, setExamCategories] = useState([]);
  const [selectedExamCategory, setSelectedExamCategory] = useState(null);
  const [examsLoading, setExamsLoading] = useState(false);
  const [examsError, setExamsError] = useState(null);

  const [abroadExamCategories, setAbroadExamCategories] = useState([]);
  const [selectedAbroadExamCategory, setSelectedAbroadExamCategory] = useState(null);
  const [abroadExamsLoading, setAbroadExamsLoading] = useState(false);
  const [abroadExamsError, setAbroadExamsError] = useState(null);

  const [aptitudeCategories, setAptitudeCategories] = useState([]);
  const [selectedAptitudeCategory, setSelectedAptitudeCategory] = useState(null);
  const [selectedSubdomain, setSelectedSubdomain] = useState(null);
  const [aptitudeLoading, setAptitudeLoading] = useState(false);
  const [aptitudeError, setAptitudeError] = useState(null);

  const selectedTopicIds = selectedAptitudeTopics.map((t) => t.id);
  const selectedExamIds = selectedExams.map((e) => e.id);

  /* Progress calculation */
  const progressSteps = [
    selectedCourse,
    selectedState,
    selectedExams.length > 0,
    selectedAptitudeTopics.length > 0,
  ].filter(Boolean).length;
  const progressPct = (progressSteps / 4) * 100;

  /* ── Fetch aptitude topics ── */
  useEffect(() => {
    const fetchAptitudeTopics = async () => {
      try {
        setAptitudeLoading(true);
        setAptitudeError(null);
        const response = await portfolioService.getAptitudeTopics({ grouped: true });
        const list = response?.data?.data || response?.data || [];
        const data = Array.isArray(list) ? list : [];
        setAptitudeCategories(data);
        if (data.length > 0) {
          setSelectedAptitudeCategory(data[0].category);
          setSelectedSubdomain(data[0].subdomains?.[0]?.subdomain || null);
        }
      } catch (error) {
        console.error('Error fetching aptitude topics:', error);
        setAptitudeError('Failed to load aptitude topics. Please try again.');
        setAptitudeCategories([]);
      } finally {
        setAptitudeLoading(false);
      }
    };
    fetchAptitudeTopics();
  }, []);

  /* ── Fetch domestic exams ── */
  useEffect(() => {
    const fetchDomesticExams = async () => {
      if (examType !== 'admission') {
        setExamCategories([]);
        setSelectedExamCategory(null);
        return;
      }
      try {
        setExamsLoading(true);
        setExamsError(null);
        const params = {
          userStage: 'student',
          examPurposeType: 'admission',
          groupByCategory: true,
          isAbroad: false,
        };
        const response = await portfolioService.getExams(params);
        const groups = response?.data?.data || response?.data || [];
        const allCategories = Array.isArray(groups) ? groups : [];
        const filteredCategories = filterExamCategoriesByPlan(allCategories, currentPlan?.name);
        setExamCategories(filteredCategories);
        if (filteredCategories.length > 0) setSelectedExamCategory(filteredCategories[0].category);
      } catch (error) {
        console.error('Error fetching domestic exams:', error);
        setExamsError('Failed to load exams. Please try again.');
        setExamCategories([]);
      } finally {
        setExamsLoading(false);
      }
    };
    fetchDomesticExams();
  }, [examType]);

  /* ── Fetch abroad exams ── */
  useEffect(() => {
    const fetchAbroadExams = async () => {
      if (!abroadEducation) {
        setAbroadExamCategories([]);
        setSelectedAbroadExamCategory(null);
        return;
      }
      try {
        setAbroadExamsLoading(true);
        setAbroadExamsError(null);
        const params = {
          userStage: 'student',
          examPurposeType: 'admission',
          groupByCategory: true,
          isAbroad: true,
        };
        const response = await portfolioService.getExams(params);
        const groups = response?.data?.data || response?.data || [];
        const allCategories = Array.isArray(groups) ? groups : [];
        const filteredCategories = filterExamCategoriesByPlan(allCategories, currentPlan?.name);
        setAbroadExamCategories(filteredCategories);
        if (filteredCategories.length > 0) setSelectedAbroadExamCategory(filteredCategories[0].category);
      } catch (error) {
        console.error('Error fetching abroad exams:', error);
        setAbroadExamsError('Failed to load abroad exams. Please try again.');
        setAbroadExamCategories([]);
      } finally {
        setAbroadExamsLoading(false);
      }
    };
    fetchAbroadExams();
  }, [examType, abroadEducation]);

  /* ── Derived values ── */
  const currentCategoryExams =
    examCategories.find((c) => c.category === selectedExamCategory)?.exams || [];

  const currentAbroadCategoryExams =
    abroadExamCategories.find((c) => c.category === selectedAbroadExamCategory)?.exams || [];

  const currentAptitudeCategory = aptitudeCategories.find(
    (c) => c.category === selectedAptitudeCategory
  );

  const currentSubdomainData = currentAptitudeCategory?.subdomains.find(
    (sd) => sd.subdomain === selectedSubdomain
  );

  /* ── Handlers ── */
  const handleSubjectToggle = (subjectId) => {
    if (selectedSubjects.includes(subjectId)) {
      updateSubjects(selectedSubjects.filter((id) => id !== subjectId));
    } else {
      updateSubjects([...selectedSubjects, subjectId]);
    }
  };

  const isUniversitySelected = (universityId) =>
    selectedUniversities.some((u) => u.id === universityId);

  const handleUniversityToggle = (university) => {
    toggleUniversity(university, selectedState);
  };

  const handleAptitudeCategoryChange = (categoryName) => {
    setSelectedAptitudeCategory(categoryName);
    const cat = aptitudeCategories.find((c) => c.category === categoryName);
    setSelectedSubdomain(cat?.subdomains?.[0]?.subdomain || null);
  };

  const toggleTopicSelection = (topic) => toggleAptitudeTopic(topic);

  const toggleSubdomainSelection = (topics) => {
    const topicIds = topics.map((t) => t.id);
    const allSelected = topicIds.every((id) => selectedTopicIds.includes(id));
    if (allSelected) {
      updateSelectedAptitudeTopics(selectedAptitudeTopics.filter((t) => !topicIds.includes(t.id)));
    } else {
      const toAdd = topics
        .filter((t) => !selectedTopicIds.includes(t.id))
        .map((t) => ({
          id: t.id,
          topic: t.topic,
          category: currentAptitudeCategory?.category,
          subdomain: currentSubdomainData?.subdomain,
        }));
      updateSelectedAptitudeTopics([...selectedAptitudeTopics, ...toAdd]);
    }
  };

  const getCourseName = () => {
    if (!selectedCourse) return null;
    return courses.find((c) => c.id === selectedCourse)?.name || null;
  };

  const getSelectedSubjectNames = () =>
    selectedSubjects
      .map((subId) => subjects.find((s) => s.id === subId)?.name)
      .filter(Boolean);

  const getSelectedUniversityNames = () =>
    selectedUniversities.map((u) => u.universityName).filter(Boolean);

  /* ─────────────────────────────────────────
     RENDER
  ───────────────────────────────────────── */
  return (
    <div className="w-full min-w-0 max-w-full overflow-x-hidden space-y-3">

      {/* Hero Banner Section */}
      <div className="bg-gradient-to-r from-slate-800 via-slate-700 to-slate-800 rounded-lg shadow-lg overflow-hidden">
        <div className="relative px-6 py-8">
          {/* Decorative background pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-primary-400 rounded-full blur-3xl"></div>
          </div>
          
          {/* Content */}
          <div className="relative flex flex-col md:flex-row items-start md:items-center gap-6">
            {/* Profile Avatar */}
            <div className="flex-shrink-0">
              <div className="w-24 h-24 md:w-28 md:h-28 rounded-2xl bg-white shadow-xl flex items-center justify-center overflow-hidden border-4 border-white/20">
                <div className="w-full h-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center">
                  <span className="text-3xl md:text-4xl font-bold text-white">AP</span>
                </div>
              </div>
            </div>

            {/* Profile Info */}
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">
                Educational Portfolio
              </h1>
              <p className="text-sm text-slate-300 mb-3">
                Complete your profile for personalized recommendations
              </p>
              
              {/* Progress Section */}
              <div className="flex items-center gap-4">
                <div className="flex-1 max-w-md">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-medium text-slate-300">Profile Completion</span>
                    <span className="text-xs font-bold text-white">{progressSteps}/4 Steps</span>
                  </div>
                  <ProgressBar value={progressPct} />
                </div>
                <div className="hidden md:flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20">
                  <Briefcase className="w-4 h-4 text-white" />
                  <span className="text-sm font-semibold text-white">{Math.round(progressPct)}%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════
          SECTION 1 — Academic Details
      ══════════════════════════════════════ */}
      <SectionCard
        icon={GraduationCap}
        iconBg="#fee2e2"
        iconColor="#c33c3d"
        title="Academic details"
        subtitle="Course, specialisation & subjects"
      >
        {/* Course preference + Course — two columns */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <FieldLabel>Course preference</FieldLabel>
            <StyledSelect
              value={coursePreference || ''}
              onChange={(e) => updateCoursePreference(e.target.value)}
            >
              <option value="">Select preference</option>
              <option value="short_term">Short term</option>
              <option value="long_term">Long term</option>
              <option value="certificate">Certificate</option>
            </StyledSelect>
          </div>
          <div>
            <FieldLabel>Course</FieldLabel>
            <StyledSelect
              value={selectedCourse || ''}
              onChange={(e) => updateCourse(e.target.value ? parseInt(e.target.value) : null)}
            >
              <option value="">Select a course</option>
              {courses.map((course) => (
                <option key={course.id} value={course.id}>
                  {course.name}
                </option>
              ))}
            </StyledSelect>
            {selectedCourse && (
              <p className="mt-1 text-[11px] text-gray-400">Industry: Information Technology</p>
            )}
          </div>
        </div>

        {/* Specialisation */}
        {selectedCourse && specialisations.length > 0 && (
          <div>
            <FieldLabel>Specialisation</FieldLabel>
            <StyledSelect
              value={selectedSpecialisation || ''}
              onChange={(e) =>
                updateSpecialisation(e.target.value ? parseInt(e.target.value) : null)
              }
            >
              <option value="">Select a specialisation</option>
              {specialisations.map((spec) => (
                <option key={spec.id} value={spec.id}>
                  {spec.specialization}
                </option>
              ))}
            </StyledSelect>
          </div>
        )}

        {/* Subjects */}
        {selectedSpecialisation && subjects.length > 0 && (
          <div>
            <FieldLabel>Associated subjects</FieldLabel>
            <div className="flex flex-wrap gap-2">
              {subjects.map((subject) => (
                <Chip
                  key={subject.id}
                  active={selectedSubjects.includes(subject.id)}
                  onClick={() => handleSubjectToggle(subject.id)}
                >
                  {subject.name}
                </Chip>
              ))}
            </div>
          </div>
        )}
      </SectionCard>

      {/* ══════════════════════════════════════
          SECTION 2 — Location & Institutions
      ══════════════════════════════════════ */}
      <SectionCard
        icon={MapPin}
        iconBg="#e6f1fb"
        iconColor="#185fa5"
        title="Location & institutions"
        subtitle="State and preferred universities (up to 3)"
      >
        <div>
          <FieldLabel>State</FieldLabel>
          <StyledSelect
            value={selectedState || ''}
            onChange={(e) => updateState(e.target.value || null)}
          >
            <option value="">Select a state</option>
            {states.map((state) => (
              <option key={state.id} value={state.name}>
                {state.name}
              </option>
            ))}
          </StyledSelect>
        </div>

        {selectedState && universities.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <FieldLabel>Universities & colleges</FieldLabel>
              <span className="text-xs text-gray-400">
                {selectedUniversities.length} / 3 selected
              </span>
            </div>

            <div className="border border-gray-100 rounded-xl bg-gray-50 overflow-hidden">
              <div className="divide-y divide-gray-100 max-h-52 overflow-y-auto">
                {universities.map((university) => {
                  const selected = isUniversitySelected(university.id);
                  const limitReached = !selected && selectedUniversities.length >= 3;
                  return (
                    <label
                      key={university.id}
                      className={`flex items-center gap-2.5 px-3 py-2 cursor-pointer transition-colors ${
                        selected ? 'bg-blue-50' : 'hover:bg-white'
                      } ${limitReached ? 'opacity-40 pointer-events-none' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={() => handleUniversityToggle(university)}
                        disabled={limitReached}
                        className="w-4 h-4 accent-blue-600 flex-shrink-0"
                      />
                      <span className="text-sm text-gray-900 flex-1 truncate font-medium">
                        {university.universityName}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>

            {selectedUniversities.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {selectedUniversities.map((u) => (
                  <RemoveTag
                    key={u.id}
                    label={u.universityName}
                    onRemove={() => toggleUniversity(u, u.state)}
                    colorClass="blue"
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </SectionCard>

      {/* ══════════════════════════════════════
          SECTION 3 — Exams & Preferences
      ══════════════════════════════════════ */}
      <SectionCard
        icon={FileText}
        iconBg="#eaf3de"
        iconColor="#3b6d11"
        title="Exams & preferences"
        subtitle="Entrance tests and personal flags"
      >
        {/* Exam type selector */}
        <div>
          <FieldLabel>Exam type</FieldLabel>
          <StyledSelect value={examType || ''} onChange={(e) => updateExamType(e.target.value)}>
            <option value="">Select exam type</option>
            <option value="admission">National exam for admission</option>
            {(careerStage === 'fresher' || careerStage === 'professional') && (
              <option value="job">Exam for job</option>
            )}
          </StyledSelect>
        </div>

        {/* Custom exam input */}
        {(examType === 'custom' || examType === 'other') && (
          <div>
            <FieldLabel>Specify exam name</FieldLabel>
            <input
              type="text"
              value={customExam}
              onChange={(e) => updateCustomExam(e.target.value)}
              placeholder="Enter exam name"
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl bg-white text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-[#c33c3d]/20 focus:border-[#c33c3d]"
            />
          </div>
        )}

        {/* Job exams */}
        {examType === 'job' && (
          <div className="space-y-3">
            <FieldLabel>Sector</FieldLabel>
            <div className="grid grid-cols-2 gap-3">
              {[
                { value: 'govt', label: 'Government' },
                { value: 'private', label: 'Private' },
              ].map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => updateOpportunityType(opt.value)}
                  className={`py-3 rounded-xl border text-sm font-semibold transition-all ${
                    opportunityType === opt.value
                      ? 'border-[#c33c3d] bg-[#fee2e2] text-[#c33c3d]'
                      : 'border-gray-200 text-gray-700 hover:border-gray-300 bg-white'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {opportunityType && (
              <div className="flex flex-col items-center justify-center text-center bg-gray-50 border border-dashed border-gray-200 rounded-xl px-4 py-8">
                <Award className="w-8 h-8 text-gray-300 mb-2" />
                <p className="text-sm font-semibold text-gray-600">Coming soon</p>
                <p className="text-xs text-gray-400 mt-1">
                  {opportunityType === 'govt' ? 'Government' : 'Private'} sector job exams will
                  be available here shortly.
                </p>
              </div>
            )}
          </div>
        )}

        {/* National admission exams */}
        {examType === 'admission' && (
          <div className="space-y-3">
            <FieldLabel>National admission exams</FieldLabel>

            {examsLoading && <LoadingRow text="Loading exams…" />}
            {!examsLoading && examsError && <ErrorMsg text={examsError} />}
            {!examsLoading && !examsError && examCategories.length === 0 && (
              <EmptyMsg text="No exams available." />
            )}

            {!examsLoading && !examsError && examCategories.length > 0 && (
              <>
                {/* Category tabs */}
                <div className="flex flex-wrap gap-2">
                  {examCategories.map((cat) => (
                    <Chip
                      key={cat.category}
                      active={cat.category === selectedExamCategory}
                      onClick={() => setSelectedExamCategory(cat.category)}
                    >
                      {cat.category}
                      <span className="ml-1.5 text-[10px] font-bold opacity-70">{cat.count}</span>
                    </Chip>
                  ))}
                </div>

                {/* Exam cards grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {currentCategoryExams.map((exam) => (
                    <ExamCard
                      key={exam.id}
                      exam={exam}
                      isSelected={selectedExamIds.includes(exam.id)}
                      onToggle={toggleExam}
                    />
                  ))}
                </div>

                {/* Selected exams summary */}
                {selectedExams.length > 0 && (
                  <div className="pt-3 border-t border-gray-100">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-gray-700 flex items-center gap-1.5">
                        <CheckCircle2 className="w-3.5 h-3.5 text-[#c33c3d]" />
                        Selected ({selectedExams.length})
                      </span>
                      <button
                        type="button"
                        onClick={() => updateSelectedExams([])}
                        className="text-[11px] font-semibold text-[#c33c3d] hover:underline"
                      >
                        Clear all
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {selectedExams.map((exam) => (
                        <RemoveTag
                          key={exam.id}
                          label={exam.examName}
                          onRemove={() => toggleExam(exam)}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Preferences section - always visible */}
        <div>
          <FieldLabel>Preferences</FieldLabel>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <label className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg cursor-pointer hover:border-gray-400 hover:bg-gray-50 transition-colors">
              <Plane className="w-4 h-4 text-blue-500 flex-shrink-0" />
              <input
                type="checkbox"
                checked={abroadEducation}
                onChange={(e) => updateAbroadEducation(e.target.checked)}
                className="w-4 h-4 border-gray-300 rounded flex-shrink-0"
              />
              <span className="text-sm font-medium text-gray-800 flex-1">Abroad education</span>
            </label>

            <label className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg cursor-pointer hover:border-gray-400 hover:bg-gray-50 transition-colors">
              <Heart className="w-4 h-4 text-red-500 flex-shrink-0" />
              <input
                type="checkbox"
                checked={medicalIssue}
                onChange={(e) => updateMedicalIssue(e.target.checked)}
                className="w-4 h-4 border-gray-300 rounded flex-shrink-0"
              />
              <span className="text-sm font-medium text-gray-800 flex-1">Medical issue</span>
            </label>
          </div>
        </div>

        {/* Abroad entrance exams */}
        {abroadEducation && (
          <div className="space-y-3 pt-1">
            <div className="flex items-center gap-2">
              <div className="h-px flex-1 bg-gray-100" />
              <FieldLabel>Abroad entrance exams</FieldLabel>
              <div className="h-px flex-1 bg-gray-100" />
            </div>

            {abroadExamsLoading && <LoadingRow text="Loading abroad exams…" />}
            {!abroadExamsLoading && abroadExamsError && <ErrorMsg text={abroadExamsError} />}
            {!abroadExamsLoading && !abroadExamsError && abroadExamCategories.length === 0 && (
              <EmptyMsg text="No abroad exams available." />
            )}

            {!abroadExamsLoading && !abroadExamsError && abroadExamCategories.length > 0 && (
              <>
                <div className="flex flex-wrap gap-2">
                  {abroadExamCategories.map((cat) => (
                    <Chip
                      key={cat.category}
                      active={cat.category === selectedAbroadExamCategory}
                      onClick={() => setSelectedAbroadExamCategory(cat.category)}
                      colorClass="navbar"
                    >
                      {cat.category}
                      <span className="ml-1.5 text-[10px] font-bold opacity-70">{cat.count}</span>
                    </Chip>
                  ))}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {currentAbroadCategoryExams.map((exam) => (
                    <ExamCard
                      key={exam.id}
                      exam={exam}
                      isSelected={selectedExamIds.includes(exam.id)}
                      onToggle={toggleExam}
                      accentColor="#1a2642"
                      accentBg="#e8eaf0"
                      tagBg="#e8eaf0"
                      tagText="#1a2642"
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </SectionCard>

      {/* ══════════════════════════════════════
          SECTION 4 — Aptitude Preparation
      ══════════════════════════════════════ */}
      <SectionCard
        icon={Target}
        iconBg="#faeeda"
        iconColor="#854f0b"
        title="Aptitude preparation"
        subtitle="Pick a category → subdomain → topics"
      >
        {aptitudeLoading && <LoadingRow text="Loading topics…" />}
        {!aptitudeLoading && aptitudeError && <ErrorMsg text={aptitudeError} />}
        {!aptitudeLoading && !aptitudeError && aptitudeCategories.length === 0 && (
          <EmptyMsg text="No aptitude topics available." />
        )}

        {!aptitudeLoading && !aptitudeError && aptitudeCategories.length > 0 && (
          <>
            {/* Layout: left rail (categories) + right panel */}
            <div className="flex gap-4">
              {/* Category rail */}
              <div className="flex flex-col gap-1 flex-shrink-0 w-28 sm:w-36">
                <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1 px-1">
                  Category
                </p>
                {aptitudeCategories.map((cat) => (
                  <button
                    key={cat.category}
                    type="button"
                    onClick={() => handleAptitudeCategoryChange(cat.category)}
                    className={`text-left px-3 py-2 rounded-xl text-xs font-semibold transition-all truncate ${
                      cat.category === selectedAptitudeCategory
                        ? 'bg-[#c33c3d] text-white'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    {cat.category}
                  </button>
                ))}
              </div>

              {/* Vertical separator */}
              <div className="w-px bg-gray-200 flex-shrink-0"></div>

              {/* Right panel */}
              <div className="flex-1 min-w-0 space-y-3">
                {/* Subdomains */}
                {currentAptitudeCategory && currentAptitudeCategory.subdomains.length > 0 && (
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1.5">
                      Subdomain
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {currentAptitudeCategory.subdomains.map((sd) => {
                        const count = sd.topics.filter((t) => selectedTopicIds.includes(t.id)).length;
                        return (
                          <button
                            key={sd.subdomain}
                            type="button"
                            onClick={() => setSelectedSubdomain(sd.subdomain)}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium border transition-all ${
                              sd.subdomain === selectedSubdomain
                                ? 'bg-[#c33c3d] border-[#c33c3d] text-white'
                                : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                            }`}
                          >
                            {sd.subdomain}
                            {count > 0 && (
                              <span className="inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[9px] font-bold bg-[#c33c3d] text-white">
                                {count}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Topics */}
                {currentSubdomainData && (
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400">
                        Topics &nbsp;
                        <span className="font-normal normal-case tracking-normal text-gray-300">
                          {currentSubdomainData.topics.filter((t) => selectedTopicIds.includes(t.id)).length}/
                          {currentSubdomainData.topics.length}
                        </span>
                      </p>
                      <button
                        type="button"
                        onClick={() => toggleSubdomainSelection(currentSubdomainData.topics)}
                        className="text-[10px] font-semibold text-[#c33c3d] hover:underline"
                      >
                        {currentSubdomainData.topics.every((t) => selectedTopicIds.includes(t.id))
                          ? 'Clear all'
                          : 'Select all'}
                      </button>
                    </div>
                    <div className="bg-gray-50 border border-gray-100 rounded-xl p-3 flex flex-wrap gap-2 max-h-44 overflow-y-auto">
                      {currentSubdomainData.topics.map((t) => {
                        const checked = selectedTopicIds.includes(t.id);
                        return (
                          <button
                            key={t.id}
                            type="button"
                            onClick={() =>
                              toggleTopicSelection({
                                id: t.id,
                                topic: t.topic,
                                category: currentAptitudeCategory?.category,
                                subdomain: currentSubdomainData?.subdomain,
                              })
                            }
                            className={`px-3 py-1.5 rounded-full text-[11px] font-medium transition-all border ${
                              checked
                                ? 'bg-[#c33c3d] border-[#c33c3d] text-white shadow-sm'
                                : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                            }`}
                          >
                            {t.topic}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Selected topics summary */}
            {selectedAptitudeTopics.length > 0 && (
              <div className="pt-3 border-t border-gray-100">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-gray-700 flex items-center gap-1.5">
                    <CheckCircle2 className="w-3.5 h-3.5 text-amber-600" />
                    Your selection ({selectedAptitudeTopics.length})
                  </span>
                  <button
                    type="button"
                    onClick={() => updateSelectedAptitudeTopics([])}
                    className="text-[11px] font-semibold text-[#c33c3d] hover:underline"
                  >
                    Clear all
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {selectedAptitudeTopics.map((t) => (
                    <span
                      key={t.id}
                      className="inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 bg-amber-50 border border-amber-200 rounded-full text-[11px] text-amber-800 font-medium"
                    >
                      <span className="text-amber-400 font-normal text-[10px]">{t.subdomain}</span>
                      <span className="text-amber-300">/</span>
                      {t.topic}
                      <button
                        type="button"
                        onClick={() => toggleAptitudeTopic(t)}
                        className="ml-0.5 w-4 h-4 rounded-full flex items-center justify-center bg-amber-100 hover:bg-amber-200 text-amber-700"
                        aria-label={`Remove ${t.topic}`}
                      >
                        <X className="w-2.5 h-2.5" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </SectionCard>

      {/* Portfolio Summary */}
      {(getCourseName() || selectedSubjects.length > 0 || coursePreference || selectedState || selectedUniversities.length > 0) && (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-primary-50 flex items-center justify-center">
              <BookOpen className="w-4 h-4 text-primary-600" />
            </div>
            <h3 className="text-sm font-semibold text-gray-900">Portfolio Summary</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {getCourseName() && (
              <div className="flex items-start gap-2.5 p-2.5 bg-gradient-to-br from-primary-50 to-white rounded-lg border border-primary-100">
                <div className="w-7 h-7 rounded-md bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <GraduationCap className="w-3.5 h-3.5 text-primary-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] text-gray-500 mb-0.5 font-medium">Course</p>
                  <p className="text-sm font-semibold text-gray-900 truncate">{getCourseName()}</p>
                </div>
              </div>
            )}

            {selectedSubjects.length > 0 && (
              <div className="flex items-start gap-2.5 p-2.5 bg-gradient-to-br from-blue-50 to-white rounded-lg border border-blue-100">
                <div className="w-7 h-7 rounded-md bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <BookOpen className="w-3.5 h-3.5 text-blue-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] text-gray-500 mb-0.5 font-medium">Subjects</p>
                  <p className="text-sm font-semibold text-gray-900 break-words">
                    {getSelectedSubjectNames().join(', ')}
                  </p>
                </div>
              </div>
            )}

            {selectedState && (
              <div className="flex items-start gap-2.5 p-2.5 bg-gradient-to-br from-green-50 to-white rounded-lg border border-green-100">
                <div className="w-7 h-7 rounded-md bg-green-100 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-3.5 h-3.5 text-green-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] text-gray-500 mb-0.5 font-medium">State</p>
                  <p className="text-sm font-semibold text-gray-900 truncate">{selectedState}</p>
                </div>
              </div>
            )}

            {selectedUniversities.length > 0 && (
              <div className="flex items-start gap-2.5 p-2.5 bg-gradient-to-br from-purple-50 to-white rounded-lg border border-purple-100">
                <div className="w-7 h-7 rounded-md bg-purple-100 flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-3.5 h-3.5 text-purple-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] text-gray-500 mb-0.5 font-medium">Institutions</p>
                  <p className="text-sm font-semibold text-gray-900 break-words">
                    {getSelectedUniversityNames().join(', ')}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────
   Router — same as original
───────────────────────────────────────────── */
const Portfolio = () => {
  const { careerStage } = usePortfolio();

  if (careerStage === 'fresher') return <FresherPortfolio />;
  if (careerStage === 'professional') return <ProfessionalPortfolio />;
  return <StudentPortfolio />;
};

export default Portfolio;